import type { ParsedBillingItem } from 'ui/types/cartTypes';
import clsx from 'clsx';
import { getFormatters } from 'ui/hooks/formatter';
import { useShallow } from 'zustand/react/shallow';
import { useSiteStore } from '@/store/SiteStoreProvider';
import BillingDetailsV2 from '../BillingDetailsV2/BillingDetailsV2';

interface BillingDetailsProps {
  billingDetails: ParsedBillingItem[];
}

export default function BillingDetails({
  billingDetails,
}: BillingDetailsProps): JSX.Element {
  const { language, region, currency } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      region: state.region,
      language: state.language,
    })),
  );
  const { formatPrice } = getFormatters({ currency, language, region });
  const useV2 = true;

  const generateContainerStyle = (isIncludedTax: boolean) =>
    clsx(
      'flex w-full flex-row items-center justify-between',
      isIncludedTax ? 'pt-5' : '',
    );

  const generateTitleStyle = (isTotal: boolean, isIncludedTax: boolean) =>
    clsx(
      isTotal ? 'font-semibold' : '',
      isIncludedTax ? 'text-inactive' : '',
      'text-right text-base tracking-normal',
    );

  const generatePriceStyle = (
    hasValue: boolean,
    isTotal: boolean,
    isIncludedTax: boolean,
  ) =>
    clsx(
      !hasValue || isIncludedTax ? 'text-inactive' : '',
      isTotal ? 'font-semibold' : '',
      'text-right text-base tracking-normal',
    );

  return (
    <>
      {useV2 ? (
        <BillingDetailsV2 billingDetails={billingDetails} />
      ) : (
        <div className='flex w-full flex-col items-center justify-center'>
          {billingDetails.map((billingItem) => {
            const hasValue = Boolean(billingItem.value);
            const isTotal = billingItem.type === 'TOTAL';
            const isIncludedTax = billingItem.type === 'INCLUDED_TAX';
            const formattedPrice = billingItem.value
              ? formatPrice(billingItem.value.centAmount, {
                  showCents: true,
                })
              : '...';
            return (
              <div
                className={generateContainerStyle(isIncludedTax)}
                key={billingItem.title}
              >
                <p className={generateTitleStyle(isTotal, isIncludedTax)}>
                  {billingItem.title}
                </p>
                <p
                  className={generatePriceStyle(
                    hasValue,
                    isTotal,
                    isIncludedTax,
                  )}
                >
                  {billingItem.type === 'DISCOUNT' ? '-' : ''}
                  {formattedPrice}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
